import { askConfirm, escapeHtml } from '@colplat/ui-web';
import { apiFetch, failureMessage } from '../api.js';
import { globals } from '../globals.js';
import {
  bootstrapWebledgerClient, clearPageNotice, pageFailure, webledgerShellHtml, wireBackButton,
} from '../webledgerClient.js';

/**
 * The whole instance, out to a file and back in again.
 *
 * Deliberately plain. Everything on this page is rare, slow and
 * consequential, so none of it is a one-click affordance and none of it is
 * optimistic: buttons say what is happening, the destructive one needs words
 * typed, and nothing redraws until the server has answered.
 *
 * Super-admin only — and the server says so too. This page is reached from a
 * nav entry that is hidden for everybody else, which is tidiness; the 403 is
 * the actual control.
 */

interface StoredBackup { filename: string; bytes: number; createdAt: string }

interface BackupManifest {
  format: number;
  env: string;
  createdAt: string;
  databases: string[];
  consistent: boolean;
  attachmentCount: number;
  attachmentBytes: number;
}

/**
 * Why the server refused, when it is a refusal a person may overrule.
 *
 * `other_env` and `newer_build` are the two checks in `restoreBackup` that
 * exist to stop an accident rather than to stop a mistake, and each has an
 * override on the route. Anything else — a file that is not a zip, a dump
 * that would not replay — arrives with no reason and is offered no way past.
 */
interface RestoreFailure {
  error?: string;
  message?: string;
  reason?: 'other_env' | 'newer_build';
  detail?: Record<string, string>;
}

/** What a refused attempt was, so the override can repeat it unchanged. */
type Attempt =
  | { kind: 'stored'; filename: string }
  | { kind: 'file'; dataBase64: string; filename: string };

interface RestoreReport {
  databases: string[];
  consistent: boolean;
  schemaFrom?: Record<string, number>;
  schemaTo: Record<string, number>;
  schemaChanged: string[];
  attachmentCount: number;
  rolledBackTo: string;
}

/**
 * The archive is base64 in a JSON envelope (ADR-0020 — no multipart parser
 * in `api`), which inflates it by a third, and the server caps the envelope
 * at 128 MB. Checking here means a too-large file is refused in the file
 * picker rather than after a two-minute upload.
 */
const MAX_IMPORT_BYTES = 96 * 1024 * 1024;

export async function mountWebledgerBackup(root: HTMLElement): Promise<void> {
  root.innerHTML = shellHtml();
  wireBackButton(root);

  const client = await bootstrapWebledgerClient(root);
  if (!client) return;

  const body = root.querySelector<HTMLElement>('#wlBody')!;
  if (!client.superAdmin) {
    body.innerHTML = '<p class="text-secondary">Backup and restore is for super-admins.</p>';
    return;
  }
  body.innerHTML = pageHtml();

  const page = new BackupPage(root, client.token, client.notify, pageFailure(root, client.notify));
  await page.refresh();
  page.wire();
}

class BackupPage {
  private backups: StoredBackup[] = [];

  /** The words the server wants typed before it will overwrite anything. */
  private confirmation = 'replace everything';

  constructor(
    private readonly root: HTMLElement,
    private readonly token: string,
    private readonly notify: (message: string) => void,
    /**
     * Announce it *and* put it on screen.
     *
     * Everything on this page is slow, rare and consequential, and every
     * failure path here used to go to `notify` alone — the `.cf-sr` region,
     * which is one pixel square and clipped to nothing. So a server that
     * answered "that backup is from prod and this is test" produced, for a
     * sighted operator, no change of any kind: the button was pressed, and
     * the page sat there. Reported exactly that way, twice.
     */
    private readonly fail: (message: string) => void,
  ) {}

  private el<T extends HTMLElement>(id: string): T {
    return this.root.querySelector<T>(id)!;
  }

  async refresh(): Promise<void> {
    const res = await apiFetch('/admin/backups', { token: this.token }).catch(() => undefined);
    if (!res?.ok) {
      this.el('#bkList').innerHTML = `<p class="text-danger small mb-0">${
        escapeHtml(await failureMessage(res, 'Could not list the backups on this server.'))}</p>`;
      return;
    }
    const data = await res.json() as { backups: StoredBackup[]; confirmation: string };
    this.backups = data.backups;
    this.confirmation = data.confirmation;
    this.el('#bkList').innerHTML = listHtml(this.backups);
    this.el<HTMLElement>('#bkConfirmWords').textContent = this.confirmation;
  }

  wire(): void {
    this.el('#bkCreate').addEventListener('click', () => { void this.create(); });
    this.el('#bkFile').addEventListener('change', () => { this.onFilePicked(); });
    this.el('#bkImport').addEventListener('click', () => { void this.importFile(); });

    // One listener for the whole table: the rows are replaced on every
    // refresh, and per-row listeners would have to be re-attached each time.
    this.el('#bkList').addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('button[data-act]');
      if (!btn) return;
      const name = btn.dataset['name']!;
      if (btn.dataset['act'] === 'download') void this.download(name);
      if (btn.dataset['act'] === 'delete') void this.remove(name);
      if (btn.dataset['act'] === 'restore') this.askRestore(name);
    });

    const words = this.el<HTMLInputElement>('#bkConfirm');
    const go = this.el<HTMLButtonElement>('#bkRestoreGo');
    // Typed exactly, or the button stays off. The server checks the same
    // string, so this only saves a round trip — but it is also the moment
    // somebody reads what they are about to do.
    words.addEventListener('input', () => { go.disabled = words.value.trim() !== this.confirmation; });
    go.addEventListener('click', () => { void this.restore(); });
    this.el('#bkRestoreCancel').addEventListener('click', () => { this.closeRestore(); });
  }

  private busy(on: boolean, message?: string): void {
    for (const b of this.root.querySelectorAll<HTMLButtonElement>('#wlBody button')) b.disabled = on;
    const note = this.el<HTMLElement>('#bkBusy');
    note.hidden = !on;
    if (message) note.textContent = message;
  }

  private async create(): Promise<void> {
    clearPageNotice(this.root);
    this.busy(true, 'Taking a backup — this reads every table and every attachment.');
    const res = await apiFetch('/admin/backups', { method: 'POST', token: this.token }).catch(() => undefined);
    this.busy(false);
    if (!res?.ok) {
      this.fail(await failureMessage(res, 'The backup failed.'));
      return;
    }
    const made = await res.json() as { filename: string; bytes: number; manifest: BackupManifest };
    await this.refresh();
    this.notify(made.manifest.consistent
      ? `Backup taken — ${formatBytes(made.bytes)}.`
      : `Backup taken — ${formatBytes(made.bytes)}. It is not a point-in-time snapshot; see the note below.`);
  }

  /**
   * Fetched rather than linked.
   *
   * The route wants an Authorization header and a plain anchor cannot send
   * one, so the bytes come through `fetch` and are handed to the browser as
   * a blob. The object URL is revoked straight after: it is a live handle on
   * a copy of the whole database sitting in this tab's memory.
   */
  private async download(name: string): Promise<void> {
    this.busy(true, 'Preparing the download…');
    const res = await apiFetch(`/admin/backups/${encodeURIComponent(name)}`, { token: this.token })
      .catch(() => undefined);
    if (!res?.ok) {
      this.busy(false);
      this.fail(await failureMessage(res, 'Could not download that backup.'));
      return;
    }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    this.busy(false);
  }

  private async remove(name: string): Promise<void> {
    const go = await askConfirm({
      title: `Delete ${name}?`,
      // The one deletion in this app that is not soft, so it says so.
      message: 'The file is gone for good — backups are not kept under "Deleted records".',
      confirm: 'Delete the file', cancel: 'Keep it', tone: 'danger',
    });
    if (!go) return;
    this.busy(true, 'Deleting…');
    const res = await apiFetch(`/admin/backups/${encodeURIComponent(name)}`, {
      method: 'DELETE', token: this.token,
    }).catch(() => undefined);
    this.busy(false);
    if (!res?.ok) {
      this.fail(await failureMessage(res, 'Could not delete that backup.'));
      return;
    }
    await this.refresh();
  }

  private askRestore(name: string): void {
    this.el<HTMLElement>('#bkRestoreName').textContent = name;
    const panel = this.el<HTMLElement>('#bkRestorePanel');
    panel.hidden = false;
    this.el<HTMLInputElement>('#bkConfirm').value = '';
    this.el<HTMLButtonElement>('#bkRestoreGo').disabled = true;
    this.el<HTMLInputElement>('#bkConfirm').focus();
  }

  private closeRestore(): void {
    this.el<HTMLElement>('#bkRestorePanel').hidden = true;
  }

  private async restore(): Promise<void> {
    const name = this.el<HTMLElement>('#bkRestoreName').textContent ?? '';
    await this.attempt({ kind: 'stored', filename: name }, {});
  }

  /**
   * One path for both ways in, so the override can repeat either.
   *
   * A stored archive and an uploaded one differ only in where the bytes come
   * from; everything after that — the confirmation, the safety backup, the
   * refusals and the overrides for them — is identical, and having written
   * it twice once already, the second copy is where the override would have
   * been forgotten.
   */
  private async attempt(attempt: Attempt, overrides: { allowOtherEnv?: boolean; allowNewer?: boolean }): Promise<void> {
    // Whatever was refused before this is no longer the thing on screen.
    // The panel holds the attempt it would repeat in its own listeners, so
    // taking it down is what forgets it.
    this.clearOverride();
    clearPageNotice(this.root);

    this.busy(true, 'Restoring. A backup of the current state is taken first — do not close this tab.');
    const res = await apiFetch(
      attempt.kind === 'stored' ? '/admin/backups/restore' : '/admin/backups/import', {
        method: 'POST', token: this.token,
        body: {
          ...(attempt.kind === 'stored'
            ? { filename: attempt.filename }
            : { dataBase64: attempt.dataBase64 }),
          confirm: this.confirmation, ...overrides,
        },
      },
    ).catch(() => undefined);
    this.busy(false);

    if (!res?.ok) {
      const failure = await readFailure(res);
      const message = failure.message ?? 'The restore failed.';
      // An override is offered for the two refusals that have one, and only
      // when it was not already in force — otherwise a server that refused
      // for the same reason twice would keep offering the button that just
      // failed.
      const already = overrides.allowOtherEnv === true || overrides.allowNewer === true;
      if (failure.reason && !already) {
        // One block, not two. What went wrong, what happens if you insist,
        // and the button that insists are one thought and belong in one
        // place — split between a banner at the top of the page and a card
        // further down, they read as two separate problems.
        this.notify(message);
        this.showOverride(attempt, failure, message);
      } else {
        this.fail(message);
      }
      return;
    }
    this.closeRestore();
    this.afterRestore(await res.json() as RestoreReport);
  }

  /**
   * The way past a refusal that is about an accident rather than a mistake.
   *
   * Deliberately a second, separate act, with its own words to read: the
   * confirmation already typed was consent to replace this instance, not
   * consent to replace it with another environment's data. Moving prod into
   * test is a real and ordinary thing to want (`restoreBackup` says so in as
   * many words) — the reverse is the one that ends a business, so into prod
   * the words have to be typed again.
   */
  private showOverride(attempt: Attempt, failure: RestoreFailure, message: string): void {
    const intoProd = globals().env === 'prod';
    const what = failure.reason === 'other_env'
      ? `Use it anyway — restore ${escapeHtml(failure.detail?.['from'] ?? 'that')} data into ${escapeHtml(globals().env)}`
      : 'Restore it anyway, onto this older build';
    const warn = failure.reason === 'other_env'
      ? `Every table in ${escapeHtml(globals().env)} will hold ${escapeHtml(failure.detail?.['from'] ?? 'the other environment')}'s rows, including its user accounts and their passwords.`
      : 'Columns that the newer schema added will be dropped, with whatever was in them.';

    const panel = this.el<HTMLElement>('#bkOverride');
    panel.innerHTML = `
      <p class="mb-2"><span aria-hidden="true">\u26a0</span> <strong>${escapeHtml(message)}</strong></p>
      <p class="small mb-2">${warn}</p>
      <div class="d-flex gap-2 align-items-start flex-wrap">
        ${intoProd ? `<input class="form-control form-control-sm" id="bkOverrideWords" autocomplete="off"
          placeholder="${escapeHtml(this.confirmation)}"
          aria-label="Type ${escapeHtml(this.confirmation)} again to confirm" style="max-width:16rem">` : ''}
        <button class="btn btn-sm btn-danger" type="button" id="bkOverrideGo"${intoProd ? ' disabled' : ''}>${what}</button>
        <button class="btn btn-sm btn-outline-secondary" type="button" id="bkOverrideNo">Cancel</button>
      </div>`;
    panel.hidden = false;

    if (intoProd) {
      const words = this.el<HTMLInputElement>('#bkOverrideWords');
      const go = this.el<HTMLButtonElement>('#bkOverrideGo');
      words.addEventListener('input', () => { go.disabled = words.value.trim() !== this.confirmation; });
    }
    this.el('#bkOverrideNo').addEventListener('click', () => { this.clearOverride(); });
    this.el('#bkOverrideGo').addEventListener('click', () => {
      void this.attempt(attempt, failure.reason === 'other_env' ? { allowOtherEnv: true } : { allowNewer: true });
    });
    // The card can be well below the fold on a server with a long list of
    // backups, and a refusal nobody can see is the bug this page already had.
    panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  private clearOverride(): void {
    const panel = this.el<HTMLElement>('#bkOverride');
    panel.hidden = true;
    panel.textContent = '';
  }

  private onFilePicked(): void {
    // The override on screen, if any, belongs to the file that was there
    // before this one.
    this.clearOverride();
    clearPageNotice(this.root);
    const file = this.el<HTMLInputElement>('#bkFile').files?.[0];
    const btn = this.el<HTMLButtonElement>('#bkImport');
    const note = this.el<HTMLElement>('#bkFileNote');
    if (!file) { btn.disabled = true; note.textContent = ''; return; }
    if (file.size > MAX_IMPORT_BYTES) {
      btn.disabled = true;
      note.textContent = `That file is ${formatBytes(file.size)} — the limit is ${formatBytes(MAX_IMPORT_BYTES)}.`;
      return;
    }
    btn.disabled = false;
    note.textContent = `${file.name} — ${formatBytes(file.size)}. Type the words below to restore it.`;
  }

  private async importFile(): Promise<void> {
    const file = this.el<HTMLInputElement>('#bkFile').files?.[0];
    if (!file) return;
    const typed = this.el<HTMLInputElement>('#bkImportConfirm').value.trim();
    if (typed !== this.confirmation) {
      // The one refusal this page makes on its own, and it was invisible
      // too: the button was pressed, nothing was sent, and nothing was said.
      this.fail(`Type "${this.confirmation}" in the box beside the button to confirm.`);
      return;
    }
    this.busy(true, 'Reading the file…');
    const dataBase64 = await base64Of(file).catch(() => undefined);
    this.busy(false);
    if (dataBase64 === undefined || dataBase64 === '') {
      this.fail('Could not read that file.');
      return;
    }
    await this.attempt({ kind: 'file', dataBase64, filename: file.name }, {});
  }

  /**
   * Every page in every open tab is now showing data that no longer exists.
   *
   * So this does not quietly refresh a list: it replaces the page with what
   * happened, what the way back is, and one button that reloads. Carrying on
   * as though a form were still editable would be the worst thing this
   * screen could do.
   */
  private afterRestore(report: RestoreReport): void {
    this.el<HTMLElement>('#wlBody').innerHTML = `
      <div class="cf-card cf-card--bleed p-3">
        <div class="cf-h mb-2">Restored</div>
        <p class="mb-2">
          ${escapeHtml(report.databases.join(', '))} replaced,
          ${report.attachmentCount} attachment${report.attachmentCount === 1 ? '' : 's'} written.
        </p>
        ${report.consistent ? '' : `<p class="small text-warning-emphasis mb-2">
          That archive was not a point-in-time snapshot, so it is worth looking over
          the most recent entries before trusting them.</p>`}
        ${report.schemaChanged.length === 0 ? '' : `<p class="small mb-2">
          That archive was made on an older version, so its tables were brought up to
          this one afterwards: ${escapeHtml(report.schemaChanged.join(', '))}. Only the
          shape was changed, never the rows.</p>`}
        <p class="small text-secondary mb-2">
          The state from just before this was saved as
          <code>${escapeHtml(report.rolledBackTo)}</code>. That is the way back.
        </p>
        <p class="small text-secondary mb-3">
          Login sessions are deliberately not carried in a backup, so everyone signed in
          anywhere — including you, here — has been signed out and will need to sign in again.
        </p>
        <button class="btn btn-primary" type="button" id="bkReload">Sign in again</button>
      </div>`;
    this.el('#bkReload').addEventListener('click', () => { window.location.reload(); });
  }
}

/**
 * Read a refusal, falling back to `failureMessage` when it is not one of ours.
 *
 * A 502 from a proxy, a connection that never arrived (`res` is `undefined`),
 * a body that is not JSON — none of those carry a reason, and all of them
 * still have to produce a sentence. `failureMessage` is what every other page
 * uses for exactly that, so the shape here is "our refusal, or whatever that
 * would have said".
 */
export async function readFailure(res: Response | undefined): Promise<RestoreFailure> {
  const body = await res?.clone().json().catch(() => undefined) as RestoreFailure | undefined;
  const message = body?.message ?? await failureMessage(res, 'The restore failed.');
  return { ...body, message };
}

/**
 * A `FileReader` rather than `Uint8Array` plus manual base64.
 *
 * Encoding by hand means building a megabytes-long string a character at a
 * time on the main thread, and `String.fromCharCode(...bytes)` on a whole
 * archive blows the argument limit outright. The reader does it natively and
 * off-thread; the only work here is dropping the data-URL prefix it adds.
 */
function base64Of(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.onload = () => {
      // `readAsDataURL` always yields a string; the union is the reader's,
      // covering the ArrayBuffer that `readAsArrayBuffer` would have given.
      const url = typeof reader.result === 'string' ? reader.result : '';
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function listHtml(backups: StoredBackup[]): string {
  if (backups.length === 0) {
    return '<p class="text-secondary small mb-0">No backups on this server yet.</p>';
  }
  const rows = backups.map((b) => `
    <tr>
      <td class="font-monospace small">${escapeHtml(b.filename)}</td>
      <td class="small text-nowrap">${escapeHtml(new Date(b.createdAt).toLocaleString())}</td>
      <td class="small text-end text-nowrap">${formatBytes(b.bytes)}</td>
      <td class="text-end text-nowrap">
        <button class="btn btn-sm btn-outline-secondary" type="button"
          data-act="download" data-name="${escapeHtml(b.filename)}">Download</button>
        <button class="btn btn-sm btn-outline-danger" type="button"
          data-act="restore" data-name="${escapeHtml(b.filename)}">Restore</button>
        <button class="btn btn-sm btn-outline-secondary" type="button"
          data-act="delete" data-name="${escapeHtml(b.filename)}"
          aria-label="Delete ${escapeHtml(b.filename)}">Delete</button>
      </td>
    </tr>`).join('');
  return `<div class="table-responsive"><table class="table table-sm align-middle mb-0">
    <thead><tr>
      <th scope="col">File</th><th scope="col">Taken</th>
      <th scope="col" class="text-end">Size</th><th scope="col"></th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table></div>`;
}

function pageHtml(): string {
  return `
    <div class="alert alert-info py-2 small" id="bkBusy" role="status" hidden></div>

    <div class="cf-card cf-card--bleed p-3 mb-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div class="cf-h">Take a backup</div>
        <button class="btn btn-sm btn-primary" type="button" id="bkCreate">Back up now</button>
      </div>
      <p class="text-secondary small mb-0">
        One file holding every table of every app and every attached receipt — enough to
        rebuild this instance on an empty server. It is kept on this server and can be
        downloaded. <strong>Treat a downloaded copy as carefully as the system itself:</strong>
        it contains every account on it, password hashes included.
      </p>
    </div>

    <div class="cf-card cf-card--bleed p-3 mb-3">
      <div class="cf-h mb-2">On this server</div>
      <div id="bkList"><p class="text-secondary small mb-0">Loading…</p></div>
    </div>

    <div class="cf-card cf-card--bleed p-3 mb-3 border-danger" id="bkOverride" hidden></div>

    <div class="cf-card cf-card--bleed p-3 mb-3" id="bkRestorePanel" hidden>
      <div class="cf-h mb-2">Restore <span class="font-monospace" id="bkRestoreName"></span></div>
      <p class="small mb-2">
        This replaces every table in every app with what the archive holds. Anything entered
        since it was taken is lost. A backup of the current state is taken first, automatically,
        and named in the result.
      </p>
      <label class="form-label small" for="bkConfirm">
        Type <strong><span id="bkConfirmWords">replace everything</span></strong> to continue
      </label>
      <div class="d-flex gap-2 align-items-start">
        <input class="form-control form-control-sm" id="bkConfirm" autocomplete="off" style="max-width:16rem">
        <button class="btn btn-sm btn-danger" type="button" id="bkRestoreGo" disabled>Restore</button>
        <button class="btn btn-sm btn-outline-secondary" type="button" id="bkRestoreCancel">Cancel</button>
      </div>
    </div>

    <div class="cf-card cf-card--bleed p-3">
      <div class="cf-h mb-2">Restore from a file</div>
      <p class="text-secondary small">
        For a server that has no backups of its own yet — a fresh box, or one being rebuilt.
        The same warning applies: everything currently here is replaced.
      </p>
      <input class="form-control form-control-sm mb-2" type="file" id="bkFile" accept=".zip,application/zip">
      <p class="small text-secondary" id="bkFileNote"></p>
      <div class="d-flex gap-2 align-items-start">
        <input class="form-control form-control-sm" id="bkImportConfirm" autocomplete="off"
          placeholder="replace everything" aria-label="Type replace everything to confirm"
          style="max-width:16rem">
        <button class="btn btn-sm btn-danger" type="button" id="bkImport" disabled>Upload and restore</button>
      </div>
    </div>

    <div class="cf-sr" aria-live="polite" aria-atomic="true"></div>`;
}

function shellHtml(): string {
  return webledgerShellHtml('backup', '<div id="wlBody"><p class="text-secondary">Loading…</p></div>');
}
